// src/pages/guia/GuiaPerfil.js
//
// Vista de solo lectura del perfil propio del Guía. Los cambios los
// hace el superadmin desde /admin/guias — aquí el Guía solo consulta.

import React, { useEffect, useRef, useState } from 'react';
import {
  Container, Typography, Card, CardContent, Stack, Box, Grid,
  LinearProgress, Divider, Alert, Chip, Button, Avatar, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import PersonIcon         from '@mui/icons-material/Person';
import QrCode2Icon        from '@mui/icons-material/QrCode2';
import DownloadIcon       from '@mui/icons-material/Download';
import PhotoCameraIcon    from '@mui/icons-material/PhotoCamera';
import LockResetIcon      from '@mui/icons-material/LockReset';
import { QRCodeCanvas } from 'qrcode.react';
import { guia as api } from '../../api/guiaApi';
import { useApp } from '../../context/AppContext';

function Campo({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
    </Box>
  );
}

export default function GuiaPerfil() {
  const { notificar } = useApp();
  const [p, setP] = useState(null);
  const [cargando, setCargando] = useState(true);
  const qrRef = useRef(null);
  const fileInputRef = useRef(null);

  // Cambio de foto
  const [subiendoFoto, setSubiendoFoto] = useState(false);

  // Cambio de contraseña
  const [dlgPass, setDlgPass] = useState(false);
  const [passActual, setPassActual] = useState('');
  const [passNuevo, setPassNuevo] = useState('');
  const [passRepetir, setPassRepetir] = useState('');
  const [guardandoPass, setGuardandoPass] = useState(false);
  const [errorPass, setErrorPass] = useState(null);

  useEffect(() => {
    api.perfil()
      .then(r => setP(r))
      .catch(e => notificar(e.mensajeUsuario || 'Error.', 'error'))
      .finally(() => setCargando(false));
  }, [notificar]);

  const descargarQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `holaveci-qr-afiliacion-${p.codigoInvitacion}.png`;
    a.click();
  };

  const onSeleccionarFoto = () => fileInputRef.current?.click();

  const onArchivoElegido = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // limpiar input para poder re-elegir el mismo archivo
    if (!file) return;
    if (file.size > 5_000_000) {
      notificar('La foto debe pesar máximo 5 MB.', 'error');
      return;
    }
    setSubiendoFoto(true);
    try {
      await api.cambiarFoto(file);
      // Refrescamos el perfil para tomar la URL nueva
      const perfil = await api.perfil();
      setP(perfil);
      notificar('Foto actualizada.', 'success');
    } catch (err) {
      notificar(err.mensajeUsuario || 'No se pudo subir la foto.', 'error');
    } finally { setSubiendoFoto(false); }
  };

  const abrirDialogPass = () => {
    setPassActual(''); setPassNuevo(''); setPassRepetir(''); setErrorPass(null);
    setDlgPass(true);
  };

  const guardarPass = async () => {
    setErrorPass(null);
    if (!passActual || !passNuevo || !passRepetir) {
      setErrorPass('Todos los campos son obligatorios.'); return;
    }
    if (passNuevo.length < 6) {
      setErrorPass('La contraseña nueva debe tener mínimo 6 caracteres.'); return;
    }
    if (passNuevo !== passRepetir) {
      setErrorPass('La contraseña nueva y su confirmación no coinciden.'); return;
    }
    if (passActual === passNuevo) {
      setErrorPass('La contraseña nueva debe ser distinta a la actual.'); return;
    }
    setGuardandoPass(true);
    try {
      await api.cambiarPassword(passActual, passNuevo);
      notificar('Contraseña actualizada.', 'success');
      setDlgPass(false);
    } catch (err) {
      setErrorPass(err.mensajeUsuario || 'No se pudo cambiar la contraseña.');
    } finally { setGuardandoPass(false); }
  };

  if (cargando) return <Container sx={{ py: 3 }}><LinearProgress /></Container>;
  if (!p) return <Container sx={{ py: 3 }}><Alert severity="warning">Sin perfil.</Alert></Container>;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <PersonIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Mi perfil</Typography>
          <Typography variant="body2" color="text.secondary">
            Si necesitas cambiar algún dato, contacta al administrador.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" mb={2}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={p.fotoUrl || undefined}
                sx={{ width: 72, height: 72, bgcolor: 'grey.200' }}
              >
                {!p.fotoUrl && <PersonIcon sx={{ fontSize: 40, color: 'grey.500' }} />}
              </Avatar>
              <IconButton
                size="small"
                onClick={onSeleccionarFoto}
                disabled={subiendoFoto}
                sx={{
                  position: 'absolute', bottom: -4, right: -4,
                  bgcolor: 'primary.main', color: 'white', p: 0.5,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
                title="Cambiar foto"
              >
                <PhotoCameraIcon fontSize="small" />
              </IconButton>
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp"
                onChange={onArchivoElegido}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontFamily="Fraunces">{p.nombreCompleto}</Typography>
                <Chip label={p.estado} size="small"
                      color={p.estado === 'ACTIVO' ? 'success' : 'warning'} />
              </Stack>
              <Button
                size="small"
                startIcon={<LockResetIcon />}
                onClick={abrirDialogPass}
                sx={{ mt: 0.5 }}
              >
                Cambiar contraseña
              </Button>
            </Box>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6} md={4}><Campo label="Documento" value={`${p.tipoDocumento} ${p.numeroDocumento}`} /></Grid>
            <Grid item xs={6} md={4}><Campo label="Teléfono"  value={p.telefono} /></Grid>
            <Grid item xs={12} md={4}><Campo label="Correo"   value={p.correo} /></Grid>
            <Grid item xs={12}><Campo label="RUT" value={p.rut} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <QrCode2Icon color="primary" />
            <Typography variant="h6" fontFamily="Fraunces">Mi código de afiliación</Typography>
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" mb={2}>
            El tendero escanea este QR desde la app HolaVeci Negocio para afiliarse
            a través tuyo. Descárgalo y guárdalo en tu celular para mostrarlo cuando
            visites una tienda.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
            <Box ref={qrRef} sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #eee' }}>
              <QRCodeCanvas value={p.codigoInvitacion || ''} size={180} level="M" marginSize={2} />
            </Box>
            <Stack spacing={1} alignItems={{ xs: 'center', sm: 'flex-start' }}>
              <Typography variant="caption" color="text.secondary">Código (por si el QR falla)</Typography>
              <Typography variant="h5" fontFamily="monospace" letterSpacing={2}>
                {p.codigoInvitacion || '—'}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={descargarQr}
                disabled={!p.codigoInvitacion}
              >
                Descargar QR
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontFamily="Fraunces" gutterBottom>Zona de operación</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}><Campo label="Dirección base" value={p.direccionBase} /></Grid>
            <Grid item xs={6} md={4}><Campo label="Municipio" value={p.municipio} /></Grid>
            <Grid item xs={6} md={4}><Campo label="Radio" value={`${p.radioOperacionKm} km`} /></Grid>
            <Grid item xs={12} md={4}><Campo label="Coordenadas"
              value={p.latitud && p.longitud ? `${p.latitud}, ${p.longitud}` : null} /></Grid>
            <Grid item xs={12}><Campo label="Barrios cubiertos" value={p.barriosCubiertos} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" fontFamily="Fraunces" gutterBottom>Modelo económico</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}><Campo label="Comisión" value={`${p.porcentajeComision}%`} /></Grid>
            <Grid item xs={6} md={3}><Campo label="Duración recurrente"
              value={p.mesesRecurrente > 0 ? `${p.mesesRecurrente} meses` : 'Sin fin'} /></Grid>
            <Grid item xs={6} md={3}><Campo label="Bono al día"
              value={`${p.diasParaBono}`} /></Grid>
            <Grid item xs={6} md={3}><Campo label="Bono"
              value={`$${(p.bonoAfiliacionInicial || 0).toLocaleString('es-CO')}`} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" fontFamily="Fraunces" gutterBottom>Datos bancarios</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={6} md={4}><Campo label="Banco" value={p.banco} /></Grid>
            <Grid item xs={6} md={4}><Campo label="Tipo cuenta" value={p.tipoCuenta} /></Grid>
            <Grid item xs={12} md={4}><Campo label="Cuenta" value={p.cuentaBancaria} /></Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={dlgPass} onClose={() => !guardandoPass && setDlgPass(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cambiar contraseña</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Contraseña actual" type="password" fullWidth
              value={passActual} onChange={(e) => setPassActual(e.target.value)}
              disabled={guardandoPass}
            />
            <TextField
              label="Contraseña nueva" type="password" fullWidth
              value={passNuevo} onChange={(e) => setPassNuevo(e.target.value)}
              disabled={guardandoPass}
              helperText="Mínimo 6 caracteres"
            />
            <TextField
              label="Repetir contraseña nueva" type="password" fullWidth
              value={passRepetir} onChange={(e) => setPassRepetir(e.target.value)}
              disabled={guardandoPass}
            />
            {errorPass && <Alert severity="error">{errorPass}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgPass(false)} disabled={guardandoPass}>Cancelar</Button>
          <Button variant="contained" onClick={guardarPass} disabled={guardandoPass}>
            {guardandoPass ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
